"use client";
/**
 * Auth state management.
 *
 * Provides:
 *   - AuthContext: current user (null = unauthenticated)
 *   - AuthProvider: wraps the app, loads token on mount, exposes login/logout
 *   - useAuth(): hook for consuming auth state in any Client Component
 *
 * Token strategy (MVP-0):
 *   Token is stored in localStorage ("wm_token").  This is readable by
 *   JavaScript on the same origin (XSS risk).  Accepted for MVP-0.
 *   See lib/api.ts for the full security note.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { api, clearToken, saveToken, UserPublic } from "./api";

interface AuthState {
  user: UserPublic | null;
  /** True while the initial /me check is in flight. */
  loading: boolean;
  /** Call after a successful login response to store the token and fetch /me. */
  setToken: (token: string) => Promise<void>;
  logout: () => void;
  /** WM-078: Re-fetch /me to pick up profile changes (name update, etc.). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  setToken: async () => {},
  logout: () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, attempt to restore session from stored token.
  useEffect(() => {
    api.me().then(({ data }) => {
      setUser(data ?? null);
      setLoading(false);
    });
  }, []);

  const setToken = useCallback(async (token: string) => {
    saveToken(token);
    const { data } = await api.me();
    setUser(data ?? null);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  /** WM-078: Re-fetch /me to pick up profile changes. */
  const refresh = useCallback(async () => {
    const { data } = await api.me();
    setUser(data ?? null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, setToken, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
